/**
 * 房间信息
 */
import React, { useRef, memo, useContext } from 'react';
import { connect } from 'react-redux';
import { Table, Button, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';
import { InfoCircleOutlined } from '@ant-design/icons';
import ContentBlock from '@/components/Reservation/ContentBlock';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import CardManage from '@/components/Reservation/RoomCardManage';
import RenewalRecord, { RenewalRecordRef } from '@/components/Reservation/RenewalRecord';
import IntlContext from '@/components/Intl/IntlContext';
import { OCC_TYPE_MAP, OccType } from '@/config/constant';
import { format2FullTimeStr, parseSeconds2Hour } from '@/utils/date';
import { parseYuan2Currency } from '@/utils/number';

import { createStateSelector } from '@/redux/actions/reservation/helper';
import { handleRecordItemChange } from '@/redux/actions/reservation/checkOut';
import { ReducerState, RentRecordInfo, storeName } from '@/redux/reducers/reservation/checkOut';

import CheckCard from '../CheckCard';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRoomList, cardEnabled } = state;
  return { rentRoomList, cardEnabled };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callItemChange: handleRecordItemChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps {
  refreshRoomCardInfo: () => void;
  refreshOrderDetail: () => void;
}

const Index: React.FC<IProps & MapStateProps & MapDispatchProps> = (props) => {
  const {
    rentRoomList,
    cardEnabled,
    callItemChange,
    refreshRoomCardInfo,
    refreshOrderDetail,
  } = props;
  const renewalRecordRef = useRef<RenewalRecordRef>(null);
  const checkCardRef = useRef<{ showModal: (...arg) => void }>({ showModal: () => {} });
  const cardManageRef = useRef<{ showModal: (...arg) => void }>({ showModal: () => {} });
  const intlCtx = useContext(IntlContext);

  // 实收超时房费
  const handlePriceChange: (overtimePrice: string, record: RentRecordInfo) => void = (
    overtimePrice,
    record,
  ) => {
    // if (PRICE_REG.test(overtimePrice)) {
    callItemChange(record.indexId, { overtimePrice });
    // }
  };

  const renderPriceItem: (value: string, record: RentRecordInfo) => React.ReactNode = (
    value,
    record,
  ) => {
    return (
      <InputWithCoupon
        withCoupon={false}
        value={value}
        prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
        onChange={(val) => handlePriceChange(val, record)}
      />
    );
  };

  // 续住明细
  const showRenewalDetail = (record: RentRecordInfo) => {
    renewalRecordRef.current.openModal(record.recNo);
  };

  // 续住明细编辑
  const handleRecordEdit = (isModify) => {
    if (isModify) {
      // 数据有变更 更新详情
      refreshOrderDetail();
    }
  };

  // 退房卡
  const showCheckCardModal = () => {
    checkCardRef.current.showModal(
      rentRoomList.map((item) => item.roomId),
      rentRoomList.map((item) => item.roomNo),
      refreshRoomCardInfo,
    );
  };

  // 房卡管理
  const showCardManage = (record) => {
    const { checkinTime, expectedDepartureTime, recNo, roomId } = record;
    cardManageRef.current.showModal(
      {
        expectedArrivalTime: checkinTime,
        expectedDepartureTime,
        recNo,
        roomId,
      },
      refreshRoomCardInfo,
    );
  };

  const tableColumns: ColumnsType<RentRecordInfo> = [
    { title: intl.get('app_constants_room_type'), dataIndex: 'roomTypeName', key: 'roomTypeName' },
    { title: intl.get('app_constants_room_no'), dataIndex: 'roomNo', key: 'roomNo' },
    {
      title: intl.get('app_reservation_occ_type'),
      dataIndex: 'occType',
      key: 'occType',
      render: (val, record) => {
        if (record.extensionFlag) {
          // 有续住 就显示多种
          return intl.get('app_enum_occ_type_multi');
        }
        return OCC_TYPE_MAP[val] || val;
      },
    },
    ...(cardEnabled
      ? [
          {
            title: intl.get('drawer_checkout_room_column_card_count'),
            dataIndex: 'roomCardCount',
            key: 'roomCardCount',
            render: (val) => val || 0,
          },
        ]
      : []),
    {
      title: intl.get('app_reservation_checkin_time'),
      dataIndex: 'checkinTime',
      key: 'checkinTime',
      render: (val) => (val ? format2FullTimeStr(val) : '—'),
    },
    {
      title: intl.get('app_reservation_etd_time'),
      dataIndex: 'expectedDepartureTime',
      key: 'expectedDepartureTime',
      render: (val) => (val ? format2FullTimeStr(val) : '—'),
    },
    {
      title: intl.get('app_reservation_occ_duration'),
      dataIndex: 'occDuration',
      key: 'occDuration',
      render: (val, record) => {
        if (record.extensionFlag) {
          return intl.get('app_enum_occ_type_multi_value', { value: val });
        }
        if (record.occType === OccType.DayUse) {
          return intl.get('app_enum_occ_type_day_value', { value: val });
        }
        if (record.occType === OccType.TimeRENT) {
          return intl.get('app_enum_occ_type_time_value', { value: val });
        }
        return '—';
      },
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_value'),
      dataIndex: 'overtime',
      key: 'overtime',
      render: (val) => (val == null ? '00:00:00' : parseSeconds2Hour(val)),
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_price'),
      dataIndex: 'overtimePriceReceivable',
      key: 'overtimePriceReceivable',
      align: 'right',
      filterDropdown: true,
      filterIcon: (
        <Tooltip
          title={intl.get('drawer_checkout_room_column_overtime_price_tips')}
          placement="top"
        >
          <InfoCircleOutlined />
        </Tooltip>
      ),
      render: (val, record) => parseYuan2Currency(record?.overtimePriceReceivable || 0),
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_price_actual'),
      dataIndex: 'overtimePrice',
      key: 'overtimePrice',
      align: 'right',
      width: 240,
      render: (value, record) => renderPriceItem(value, record),
    },
    {
      title: intl.get('app_reservation_checkin_remark'),
      dataIndex: 'checkinRemark',
      key: 'checkinRemark',
      render: (value) => value || '—',
    },
    {
      title: intl.get('app_table_column_action'),
      key: 'operation',
      align: 'right',
      render: (_, record) => {
        const actionArr = [];
        const { extensionFlag, roomCardCount } = record;
        if (extensionFlag) {
          // 续住
          actionArr.push(
            <Button
              key="renewal"
              type="text"
              className={styles.deleteBtn}
              onClick={() => showRenewalDetail(record)}
            >
              {intl.get('drawer_checkout_room_content_extension_detail_btn')}
            </Button>,
          );
        }
        if (roomCardCount && cardEnabled) {
          actionArr.push(
            <Button
              key="card"
              type="text"
              className={styles.deleteBtn}
              onClick={() => showCardManage(record)}
            >
              {intl.get('drawer_checkout_room_content_card_manage_btn')}
            </Button>,
          );
        }
        return actionArr.length ? actionArr : '—';
      },
    },
  ];

  return (
    <ContentBlock
      title={intl.get('drawer_checkout_room_block_title')}
      className={styles.listContainer}
    >
      {cardEnabled ? (
        <div className={styles.topBarWrapper}>
          <Button type="primary" onClick={showCheckCardModal}>
            {intl.get('drawer_checkout_room_card_return_btn')}
          </Button>
        </div>
      ) : null}
      <Table
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="indexId"
        columns={tableColumns}
        dataSource={rentRoomList}
      />
      <RenewalRecord ref={renewalRecordRef} onEnd={handleRecordEdit} />
      <CheckCard innerRef={checkCardRef} />
      <CardManage innerRef={cardManageRef} />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps, IProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
